/**
 * PlatformUsersPage — Vista cross-tenant de usuarios para super_admin (Sprint BB/FF).
 *
 * Misma arquitectura que PlatformTenantsPage: tabla simple + paginación + modal crear.
 */
import { type FormEvent, useEffect, useState } from 'react';

import { useAppShell } from '../components/AppShell.js';
import { Button } from '../components/ui/Button.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton.js';
import {
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '../components/ui/Modal.js';
import { useAuth } from '../auth/AuthContext.js';
import { loadSession } from '../auth/storage.js';

interface UserRow {
  id: string;
  email: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  tenantId: string;
  tenant: { name: string; slug: string };
}

interface ListMeta {
  page: number;
  pageSize: number;
  total: number;
  pages: number;
}

interface TenantOption {
  id: string;
  name: string;
  slug: string;
}

function getToken(): string {
  // Fuente canónica: sessionStorage via loadSession (evita el bug de leer
  // localStorage['session'] -> token vacío -> 401 missing_token).
  return loadSession()?.token ?? '';
}

async function apiFetch(path: string, opts?: RequestInit): Promise<Response> {
  const token = getToken();
  return fetch(`/api/platform${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts?.headers ?? {}),
    },
  });
}

export function PlatformUsersPage(): JSX.Element {
  const { setBackTo } = useAppShell();
  const { session } = useAuth();

  useEffect(() => {
    setBackTo(null);
    return () => setBackTo(null);
  }, [setBackTo]);

  const [users, setUsers] = useState<UserRow[]>([]);
  const [meta, setMeta] = useState<ListMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);

  // Lista de tenants para el selector de filtro y creación
  const [tenants, setTenants] = useState<TenantOption[]>([]);

  // Modal crear usuario
  const [showCreate, setShowCreate] = useState(false);
  const [createEmail, setCreateEmail] = useState('');
  const [createTenantId, setCreateTenantId] = useState('');
  const [createRole, setCreateRole] = useState<'auditor' | 'admin'>('auditor');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // Contraseña temporal devuelta por la API — se muestra una sola vez
  const [createdPassword, setCreatedPassword] = useState<string | null>(null);

  // Cargar tenants para el filtro
  useEffect(() => {
    if (!session?.token) return;
    apiFetch('/tenants?pageSize=100')
      .then((r) => r.json() as Promise<{ data: TenantOption[] }>)
      .then(({ data }) => setTenants(data))
      .catch(() => {
        /* silencioso — el filtro simplemente no muestra opciones */
      });
  }, [session?.token]);

  // Cargar usuarios
  useEffect(() => {
    if (!session?.token) return;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page), pageSize: '20' });
    if (search) params.set('search', search);
    if (tenantFilter) params.set('tenantId', tenantFilter);
    if (roleFilter) params.set('role', roleFilter);

    apiFetch(`/users?${params.toString()}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(await r.text());
        return r.json() as Promise<{ data: UserRow[]; meta: ListMeta }>;
      })
      .then(({ data, meta: m }) => {
        setUsers(data);
        setMeta(m);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Error cargando usuarios'))
      .finally(() => setLoading(false));
  }, [page, search, tenantFilter, roleFilter, session?.token]);

  async function handleToggleActive(user: UserRow): Promise<void> {
    await apiFetch(`/users/${user.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive: !user.isActive }),
    });
    setPage((p) => p); // fuerza reload
  }

  async function handleCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    try {
      const r = await apiFetch('/users', {
        method: 'POST',
        body: JSON.stringify({ email: createEmail, tenantId: createTenantId, role: createRole }),
      });
      const body = (await r.json()) as { error?: string; temporaryPassword?: string };
      if (!r.ok) {
        const errorMap: Record<string, string> = {
          email_already_used: 'El email ya está en uso en ese tenant.',
          tenant_not_found: 'El tenant seleccionado no existe.',
          invalid_body: 'Datos inválidos. Verifica email, tenant y rol.',
        };
        setCreateError(errorMap[body.error ?? ''] ?? body.error ?? 'Error al crear el usuario.');
        return;
      }
      // Mostrar la contraseña temporal en el mismo modal — sólo se devuelve una vez
      setCreatedPassword(body.temporaryPassword ?? null);
      setCreateEmail('');
      setCreateTenantId('');
      setCreateRole('auditor');
      // Refrescar la lista volviendo a página 1
      setPage(1);
    } finally {
      setCreating(false);
    }
  }

  function handleCloseCreate(): void {
    setShowCreate(false);
    setCreateError(null);
    setCreatedPassword(null);
    setCreateEmail('');
    setCreateTenantId('');
    setCreateRole('auditor');
  }

  return (
    <main className="mx-auto max-w-6xl px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-cumple-primary-800">Usuarios</h1>
        <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
          Nuevo usuario
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="search"
          placeholder="Buscar por email…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="border border-slate-200 rounded-md px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-cumple-primary-700"
        />
        <select
          value={tenantFilter}
          onChange={(e) => {
            setTenantFilter(e.target.value);
            setPage(1);
          }}
          className="border border-slate-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cumple-primary-700"
        >
          <option value="">Todos los tenants</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} ({t.slug})
            </option>
          ))}
        </select>
        <select
          value={roleFilter}
          onChange={(e) => {
            setRoleFilter(e.target.value);
            setPage(1);
          }}
          className="border border-slate-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cumple-primary-700"
        >
          <option value="">Todos los roles</option>
          <option value="auditor">Auditor</option>
          <option value="admin">Admin</option>
          <option value="super_admin">Super Admin</option>
        </select>
      </div>

      {loading ? (
        <LoadingSkeleton variant="list" rows={5} />
      ) : error ? (
        <EmptyState variant="error" title="Error cargando usuarios" description={error} />
      ) : users.length === 0 ? (
        <EmptyState
          title="Sin usuarios"
          description="No hay usuarios que coincidan con los filtros."
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-cumple-primary-800 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Email</th>
                  <th className="px-4 py-3 text-left font-medium">Rol</th>
                  <th className="px-4 py-3 text-left font-medium">Tenant</th>
                  <th className="px-4 py-3 text-left font-medium">Estado</th>
                  <th className="px-4 py-3 text-left font-medium">Último login</th>
                  <th className="px-4 py-3 text-left font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-cumple-primary-800">{u.email}</td>
                    <td className="px-4 py-3">
                      <RoleBadge role={u.role} />
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {u.tenant.name}
                      <span className="ml-1 text-xs text-slate-400 font-mono">
                        ({u.tenant.slug})
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${u.isActive ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`}
                      >
                        {u.isActive ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('es-DO') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        variant={u.isActive ? 'danger' : 'secondary'}
                        size="sm"
                        className="text-xs min-h-[32px]"
                        onClick={() => void handleToggleActive(u)}
                      >
                        {u.isActive ? 'Desactivar' : 'Activar'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {meta && meta.pages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
              <span>
                Mostrando {(meta.page - 1) * meta.pageSize + 1}–
                {Math.min(meta.page * meta.pageSize, meta.total)} de {meta.total}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page <= 1}
                  className="min-h-[36px]"
                  onClick={() => setPage((p) => p - 1)}
                >
                  Anterior
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page >= meta.pages}
                  className="min-h-[36px]"
                  onClick={() => setPage((p) => p + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      {/* Modal crear usuario */}
      <Modal
        open={showCreate}
        onOpenChange={(o) => {
          if (!o) handleCloseCreate();
        }}
      >
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>Nuevo usuario</ModalTitle>
            <ModalDescription>Crea un usuario en el tenant elegido.</ModalDescription>
          </ModalHeader>

          {/* Paso 1: formulario de creación */}
          {!createdPassword && (
            <form onSubmit={(e) => void handleCreate(e)} className="space-y-4 mt-2">
              <div>
                <label htmlFor="cu-email" className="block text-sm font-medium text-slate-700 mb-1">
                  Email
                </label>
                <input
                  id="cu-email"
                  type="email"
                  required
                  value={createEmail}
                  onChange={(e) => setCreateEmail(e.target.value)}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cumple-primary-700"
                />
              </div>
              <div>
                <label
                  htmlFor="cu-tenant"
                  className="block text-sm font-medium text-slate-700 mb-1"
                >
                  Tenant
                </label>
                <select
                  id="cu-tenant"
                  required
                  value={createTenantId}
                  onChange={(e) => setCreateTenantId(e.target.value)}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cumple-primary-700"
                >
                  <option value="">Selecciona un tenant…</option>
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.slug})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="cu-role" className="block text-sm font-medium text-slate-700 mb-1">
                  Rol
                </label>
                <select
                  id="cu-role"
                  required
                  value={createRole}
                  onChange={(e) => setCreateRole(e.target.value as 'auditor' | 'admin')}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cumple-primary-700"
                >
                  <option value="auditor">Auditor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              {createError && (
                <p className="text-sm text-cumple-danger-600" role="alert">
                  {createError}
                </p>
              )}
              <ModalFooter>
                <Button variant="secondary" size="sm" type="button" onClick={handleCloseCreate}>
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  type="submit"
                  loading={creating}
                  loadingText="Creando…"
                >
                  Crear usuario
                </Button>
              </ModalFooter>
            </form>
          )}

          {/* Paso 2: mostrar contraseña temporal UNA sola vez */}
          {createdPassword && (
            <div className="mt-2 space-y-4">
              <div className="rounded-md bg-cumple-primary-50 border border-cumple-primary-100 p-4">
                <p className="text-sm font-medium text-cumple-primary-800 mb-2">
                  Usuario creado. Contraseña temporal:
                </p>
                <code className="block break-all rounded bg-white border border-slate-200 px-3 py-2 text-sm font-mono text-cumple-primary-700 select-all">
                  {createdPassword}
                </code>
                <p className="mt-2 text-xs text-cumple-gray-700">
                  Guárdala ahora — no se volverá a mostrar. Entrégala al usuario por canal seguro.
                  Deberá cambiarla en el próximo inicio de sesión.
                </p>
              </div>
              <ModalFooter>
                <Button variant="primary" size="sm" onClick={handleCloseCreate}>
                  Cerrar
                </Button>
              </ModalFooter>
            </div>
          )}
        </ModalContent>
      </Modal>
    </main>
  );
}

function RoleBadge({ role }: { role: string }): JSX.Element {
  const map: Record<string, string> = {
    super_admin: 'bg-cumple-accent-100 text-cumple-accent-800',
    admin: 'bg-cumple-primary-100 text-cumple-primary-800',
    auditor: 'bg-slate-100 text-slate-700',
  };
  const label: Record<string, string> = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    auditor: 'Auditor',
  };
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${map[role] ?? 'bg-slate-100 text-slate-600'}`}
    >
      {label[role] ?? role}
    </span>
  );
}
